import { useGetOne } from "ra-core";

import type { Company, Contact, Deal, Partner } from "../types";
import type { EmailDraft } from "./ComposeEmailDialog";
import type { TemplateVariables } from "./emailTemplates";
import {
  contactRecipients,
  useCompanyRecipients,
  useContactsRecipients,
} from "./recipientSuggestions";

/**
 * What the compose window of each kind of record needs: the draft (its links,
 * so the email shows in the record's feeds), known addresses, the template
 * placeholders and the company whose quotes can be attached.
 */
export interface RecordEmailProps {
  filter: Record<string, unknown>;
  draft: EmailDraft;
  suggestions: ReturnType<typeof contactRecipients>;
  variables: TemplateVariables;
  companyId?: Company["id"];
}

export const useCompanyEmailProps = (company: Company): RecordEmailProps => ({
  filter: { "company_ids@cs": `{${company.id}}` },
  draft: { links: { company_ids: [company.id] } },
  suggestions: useCompanyRecipients(company.id),
  variables: { entreprise: company.name },
  companyId: company.id,
});

export const useContactEmailProps = (contact: Contact): RecordEmailProps => {
  const recipients = contactRecipients(contact);
  const companyIds = contact.company_ids?.length
    ? contact.company_ids
    : contact.company_id != null
      ? [contact.company_id]
      : [];
  return {
    filter: { "contact_ids@cs": `{${contact.id}}` },
    draft: {
      to: recipients.slice(0, 1),
      links: { contact_ids: [contact.id], company_ids: companyIds },
    },
    suggestions: recipients,
    variables: {
      prenom: contact.first_name,
      nom: contact.last_name,
      entreprise: contact.company_name,
    },
    companyId: contact.company_id ?? undefined,
  };
};

export const usePartnerEmailProps = (partner: Partner): RecordEmailProps => {
  const recipients = partner.email
    ? [{ email: partner.email.toLowerCase(), name: partner.name }]
    : [];
  return {
    filter: { "partner_ids@cs": `{${partner.id}}` },
    draft: { to: recipients, links: { partner_ids: [partner.id] } },
    suggestions: recipients,
    variables: { prenom: partner.name, entreprise: partner.organisation },
  };
};

export const useDealEmailProps = (deal: Deal): RecordEmailProps => {
  const suggestions = useContactsRecipients(deal.contact_ids ?? []);
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: deal.company_id },
    { enabled: deal.company_id != null },
  );
  return {
    filter: { "deal_ids@cs": `{${deal.id}}` },
    draft: {
      links: {
        deal_ids: [deal.id],
        company_ids: deal.company_id != null ? [deal.company_id] : [],
        contact_ids: deal.contact_ids ?? [],
      },
    },
    suggestions,
    variables: { entreprise: company?.name },
    companyId: deal.company_id ?? undefined,
  };
};
